import React, { useState } from 'react';
import { IoAdd, IoClose } from "react-icons/io5";

interface IntentSignalCardProps {
  signal: {
    signal: string;
    keywords?: string[];
    rulesets?: string[];
  };
  onAddKeyword?: (keyword: string) => void;
  onRemoveKeyword?: (keyword: string) => void;
  onAddRuleset?: (ruleset: string) => void;
  onRemoveRuleset?: (ruleset: string) => void;
  onSave?: () => void;
  onCancel?: () => void;
}

const IntentSignalCard: React.FC<IntentSignalCardProps> = ({
  signal,
  onAddKeyword,
  onRemoveKeyword,
  onAddRuleset,
  onRemoveRuleset,
  onSave,
  onCancel
}) => {
  const [keywordInput, setKeywordInput] = useState('');
  const [rulesetInput, setRulesetInput] = useState('');

  const handleAddKeyword = (e: React.FormEvent) => {
    e.preventDefault();
    if (keywordInput.trim() && onAddKeyword) {
      onAddKeyword(keywordInput.trim());
      setKeywordInput('');
    }
  };

  const handleAddRuleset = (e: React.FormEvent) => {
    e.preventDefault();
    if (rulesetInput.trim() && onAddRuleset) {
      onAddRuleset(rulesetInput.trim());
      setRulesetInput('');
    }
  };

  const handleKeywordKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddKeyword(e);
    }
  };

  const handleRulesetKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddRuleset(e);
    }
  };

  const handleSave = () => {
    if (onSave) {
      onSave();
    }
  };

  const handleCancel = () => {
    // Clear inputs before canceling
    setKeywordInput('');
    setRulesetInput('');
    if (onCancel) {
      onCancel();
    }
  };

  return (
    <div className="flex flex-col border border-[#EEEEEE] rounded-[8px] overflow-y-auto scrollbar-hide">
        <div className="flex flex-col gap-6 p-6">
        {/* Signal Name Section */}
        <div className="flex flex-col gap-2">
            <label className="text-[12px] text-[#5C5E63] font-normal text-left">Signal name</label>
            <div className="flex flex-col gap-3">
            <div className="flex items-center gap-2">
                <input
                type="text"
                value={signal.signal}
                readOnly
                className="flex-1 px-4 h-[34px] text-[14px] font-normal border border-[#EEEEEE] rounded-[8px] bg-[#F5F5F5] text-[#7A7A7A] cursor-not-allowed"
                />
            </div>
            </div>
        </div>

        {/* Keywords Section */}
        <div className="flex flex-col gap-2">
            <label className="text-[12px] text-[#5C5E63] font-normal text-left">Edit keywords</label>
            <div className="flex flex-col gap-3">
            <div className="flex items-center gap-2">
                <input
                type="text"
                value={keywordInput}
                onChange={(e) => setKeywordInput(e.target.value)}
                onKeyPress={handleKeywordKeyPress}
                placeholder="Add keywords here"
                className="flex-1 px-4 h-[34px] w-[476px] text-[14px] font-normal border border-[#DDDDDD] rounded-[8px] focus:outline-none focus:ring-1 focus:ring-[#42E56F] text-[#9EA0A0] placeholder-[#9FA1A7] text-left"
                />
                <button 
                className={`h-[34px] w-fit whitespace-nowrap px-3 text-[14px] rounded-[8px] flex items-center gap-1 shadow-[0px_0px_2px_0px_#E0E0E0] ${
                    keywordInput.trim() 
                    ? 'bg-[#29DC5B] text-[#222222] hover:bg-[#0FAA3A]' 
                    : 'bg-[#FAFAFA] text-[#222222] cursor-not-allowed'
                }`}
                onClick={handleAddKeyword}
                disabled={!keywordInput.trim()}
                >
                + Add keyword
                </button>
            </div>
            
            <div className="flex flex-wrap gap-2">
                {signal.keywords?.map((keyword, index) => (
                <span 
                    key={index}
                    className={`inline-flex items-center gap-1 px-3 py-1 rounded-[8px] text-[14px]
                    ${index % 3 === 0 ? 'bg-[#E5F9FF] text-[#0891B2]' : 
                        index % 3 === 1 ? 'bg-[#E5FFE6] text-[#16A34A]' : 
                        'bg-[#FFE5E5] text-[#DC2626]'}`}
                >
                    {keyword}
                    <button 
                    onClick={() => {
                        const indexToRemove = signal.keywords?.indexOf(keyword) ?? -1;
                        if (indexToRemove !== -1 && onRemoveKeyword) {
                            onRemoveKeyword(keyword);
                        }
                    }}
                    className="text-current hover:text-opacity-70"
                    >
                    ×
                    </button>
                </span>
                ))}
            </div>
            </div>
        </div>

        {/* Ruleset Section */}
        <div className="flex flex-col gap-2">
            <label className="text-[12px] text-[#5C5E63] font-normal text-left">Edit ruleset</label>
            <div className="flex flex-col gap-3">
            <div className="flex items-center gap-2">
                <input
                type="text"
                value={rulesetInput}
                onChange={(e) => setRulesetInput(e.target.value)}
                onKeyPress={handleRulesetKeyPress}
                placeholder="Enter ruleset"
                className="flex-1 px-4 h-[34px] w-[476px] text-[14px] font-normal border border-[#DDDDDD] rounded-[8px] focus:outline-none focus:ring-1 focus:ring-[#42E56F] text-[#7A7A7A] placeholder-[#9FA1A7] text-left"
                />
                <button 
                className={`h-[34px] w-fit whitespace-nowrap px-3 text-[14px] rounded-[8px] flex items-center gap-1 shadow-[0px_0px_2px_0px_#E0E0E0] ${
                    rulesetInput.trim() 
                    ? 'bg-[#29DC5B] text-[#222222] hover:bg-[#0FAA3A]' 
                    : 'bg-[#FAFAFA] text-[#222222] cursor-not-allowed'
                }`}
                onClick={handleAddRuleset}
                disabled={!rulesetInput.trim()}
                >
                + Add ruleset
                </button>
            </div>
            
            <div className="flex flex-wrap gap-2">
                {signal.rulesets?.map((ruleset, index) => (
                <span 
                    key={index}
                    className={`inline-flex items-center gap-2 px-3 py-1 rounded-[8px] text-[14px]
                    ${index % 3 === 0 ? 'bg-[#E5F9FF] text-[#0891B2]' : 
                        index % 3 === 1 ? 'bg-[#E5FFE6] text-[#16A34A]' : 
                        'bg-[#FFE5E5] text-[#DC2626]'}`}
                >
                    {ruleset}
                    <button 
                    onClick={() => {
                        const indexToRemove = signal.rulesets?.indexOf(ruleset) ?? -1;
                        if (indexToRemove !== -1 && onRemoveRuleset) {
                            onRemoveRuleset(ruleset);
                        }
                    }}
                    className="text-current hover:text-opacity-70"
                    >
                    <IoClose className="w-3 h-3" />
                    </button>
                </span>
                ))}
            </div>
            </div>
        </div>
      </div>

      <div className="flex justify-end border-t border-[#EEEEEE] bg-[#FAFAFBE5]">
        <div className="flex items-center gap-2 p-2">
          <button
            className="h-[28px] w-fit whitespace-nowrap px-1 text-[14px] font-normal rounded-[8px] bg-[#FFFFFF] text-[#000000] hover:bg-[#F5F5F5]"
            style={{ boxShadow: '0px 0px 2px 0px #E0E0E0' }}
            onClick={handleCancel}
          >
            Cancel
          </button>
          <button
            className="h-[28px] w-fit whitespace-nowrap px-2 text-[14px] font-normal rounded-[8px] bg-[#29DC5B] text-[#000000] hover:bg-[#0FAA3A] shadow-[0px_0px_1px_0px_#23252940_inset]"
            onClick={handleSave}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

export default IntentSignalCard; 